package fr.arcplex.network.impl.coherence

import fr.arcplex.network.NetworkConfig
import fr.arcplex.network.cache.apiCache
import fr.arcplex.network.http.Serializer
import fr.arcplex.network.http.createNetworkHttpClient
import fr.arcplex.network.logging.Logger
import fr.arcplex.network.model.CoherenceActions
import fr.arcplex.network.model.CoherenceFilmInfo
import fr.arcplex.network.model.CoherenceImages
import fr.arcplex.network.model.CoherenceProgramContents
import fr.arcplex.network.model.Rail
import fr.arcplex.network.model.RailItem
import fr.arcplex.network.template.parseCoherenceTemplate
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.contentnegotiation.ContentNegotiation
import io.ktor.client.request.get
import io.ktor.client.request.headers
import io.ktor.serialization.kotlinx.json.json
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.intOrNull

/**
 * Film payload of the Coherence API: fetch, cache (key `coherence:film:<id>`)
 * and mapping to the unified [CoherenceFilmInfo].
 */
internal class CoherenceFilmApi(private val config: NetworkConfig) {

    private val client: HttpClient = createNetworkHttpClient(config.allowInvalidCertificates) {
        install(ContentNegotiation) {
            json(Serializer.json)
        }
    }

    suspend fun getFilmInfo(id: String): Result<CoherenceFilmInfo> {
        val cacheKey = "coherence:film:$id"

        val cachedBody = apiCache.get(cacheKey)
        if (cachedBody != null) {
            try {
                val element = Serializer.json.parseToJsonElement(cachedBody) as? JsonObject
                    ?: throw IllegalStateException("Invalid cached entry")
                return Result.success(mapToFilmInfo(element))
            } catch (e: Exception) {
                Logger.e("CoherenceApi", "cache re-parse failed, evicting $cacheKey: ${e.message}")
                apiCache.remove(cacheKey)
            }
        }

        return fetchFilmInfo(id, cacheKey)
    }

    private suspend fun fetchFilmInfo(id: String, cacheKey: String): Result<CoherenceFilmInfo> {
        return try {
            val response = client.get("${config.coherenceBaseUrl}/api/box/film/$id") {
                headers {
                    append("Accept", "application/json")
                    append("X-API-Key", config.apiKey)
                }
            }.body<JsonElement>()

            val filmInfo = mapToFilmInfo(response as JsonObject)
            try {
                apiCache.put(cacheKey, response.toString())
            } catch (e: Exception) {
                Logger.e("CoherenceApi", "cache write failed: ${e.message}")
            }
            Result.success(filmInfo)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    private fun mapToFilmInfo(json: JsonObject): CoherenceFilmInfo {
        val contentJson = json["content"] as? JsonObject
        val templateJson = json["template"] as? JsonObject

        val rawJson = json.toString()
        val boxType = json["box_type"]?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val contentType = json["content_type"]?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val topLevelTemplateRevision = json["template_revision"]?.let { it as? JsonPrimitive }?.intOrNull ?: 0
        val fallback = json["fallback"]?.let { it as? JsonPrimitive }?.contentOrNull ?: ""

        val contentData = contentJson?.get("data") as? JsonObject
        val contentBody = contentJson?.get("body")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val templateBody = templateJson?.get("body")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""

        val dataId = contentData?.get("id")?.let { it as? JsonPrimitive }?.intOrNull ?: 0
        val dataUuid = contentData?.get("uuid")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataTitle = contentData?.get("title")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataOriginalTitle = contentData?.get("original_title")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataYear = contentData?.get("year")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataDuration = contentData?.get("duration")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataDurationMinutes = contentData?.get("duration_minutes")?.let { it as? JsonPrimitive }?.intOrNull ?: 0
        val dataSynopsis = contentData?.get("synopsis")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataGenres = contentData?.get("genres")?.let { arr -> (arr as? JsonArray)?.mapNotNull { (it as? JsonPrimitive)?.contentOrNull } ?: emptyList() }
        val dataCasting = contentData?.get("casting")?.let { arr -> (arr as? JsonArray)?.mapNotNull { (it as? JsonPrimitive)?.contentOrNull } ?: emptyList() }
        val dataDirector = contentData?.get("director")?.let { arr -> (arr as? JsonArray)?.mapNotNull { (it as? JsonPrimitive)?.contentOrNull } ?: emptyList() }
        val dataCsa = contentData?.get("csa")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataCsaLabel = contentData?.get("csa_label")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataSource = contentData?.get("source")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataCarpenterId = contentData?.get("carpenter_id")?.let { it as? JsonPrimitive }?.intOrNull ?: 0

        val imagesJson = contentData?.get("images") as? JsonObject
        val dataBackdrop = imagesJson?.get("backdrop")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""
        val dataPoster = imagesJson?.get("poster")?.let { it as? JsonPrimitive }?.contentOrNull ?: ""

        val actionsJson = contentData?.get("actions") as? JsonObject
        val dataPlay = actionsJson?.get("play")?.let { it.asBoolean() } ?: false
        val dataTrailer = actionsJson?.get("trailer")?.let { it.asBoolean() } ?: false
        val dataWishlist = actionsJson?.get("wishlist")?.let { it.asBoolean() } ?: false

        val railsMap = parseRailsFromKdl(contentBody)
        Logger.d("COH", "film $dataId: contentBody=${contentBody.length}c rails=${railsMap.size} " +
            railsMap.entries.joinToString(prefix = "[", postfix = "]") { "${it.key}=${it.value.items.size}" })

        val contents = CoherenceProgramContents(
            uuid = dataUuid,
            ref = dataId,
            title = dataTitle,
            originalTitle = dataOriginalTitle,
            synopsis = dataSynopsis,
            duration = dataDuration,
            durationMinutes = dataDurationMinutes,
            year = dataYear,
            csa = dataCsa,
            csaLabel = dataCsaLabel,
            genres = dataGenres ?: emptyList(),
            casting = dataCasting ?: emptyList(),
            director = dataDirector ?: emptyList(),
            images = CoherenceImages(backdrop = dataBackdrop, poster = dataPoster),
            actions = CoherenceActions(play = dataPlay, trailer = dataTrailer, wishlist = dataWishlist),
            rails = railsMap,
            rawDataJson = contentData?.toString() ?: ""
        )

        val template = parseCoherenceTemplate(templateBody)
        Logger.d("COH", "film $dataId: templateBody=${templateBody.length}c regions=" +
            template.regions.joinToString(prefix = "[", postfix = "]") {
                "${it.name}(h=${it.height},rails=${it.rails.size},slots=${it.slots.size},layers=${it.layers.size})"
            })

        return CoherenceFilmInfo(
            contents = contents,
            template = template,
            templateBody = templateBody,
            contentBody = contentBody,
            rawJson = rawJson,
            boxType = boxType,
            contentType = contentType,
            templateRevision = topLevelTemplateRevision,
            fallback = fallback
        )
    }

    private fun JsonElement.asBoolean(): Boolean = when (this) {
        is JsonPrimitive -> booleanOrNull ?: (contentOrNull == "true")
        else -> false
    }

    private fun parseRailsFromKdl(kdlBody: String): Map<String, Rail> {
        val itemsByRail = mutableMapOf<String, MutableList<RailItem>>()
        val lines = kdlBody.lines()
        var currentRail: String? = null
        var inRailBlock = 0

        for (line in lines) {
            val trimmed = line.trim()
            if (trimmed.isEmpty()) continue

            val railMatch = """rail\s+"([^"]+)"""".toRegex().find(trimmed)
            if (railMatch != null) {
                currentRail = railMatch.groupValues[1]
                itemsByRail.getOrPut(currentRail) { mutableListOf() }
                if (trimmed.contains("{")) {
                    inRailBlock = 1
                }
                continue
            }

            if (inRailBlock > 0) {
                val openBraces = trimmed.count { it == '{' }
                val closeBraces = trimmed.count { it == '}' }
                inRailBlock += openBraces - closeBraces

                if (inRailBlock <= 0) {
                    inRailBlock = 0
                    currentRail = null
                    continue
                }

                val itemMatch = """item\s+(.*)""".toRegex().find(trimmed)
                if (itemMatch != null && currentRail != null) {
                    val itemStr = itemMatch.groupValues[1]
                    val item = parseRailItemFromKdl(itemStr)
                    if (item != null) {
                        itemsByRail[currentRail]!!.add(item)
                    }
                }
            }
        }

        return itemsByRail.mapValues { (name, items) ->
            Rail(
                id = name,
                uid = "coherence:$name",
                label = name,
                type = "coherence",
                items = items.toList()
            )
        }
    }

    private fun parseRailItemFromKdl(itemStr: String): RailItem? {
        fun extractAttr(name: String): String? {
            val match = """$name="([^"]*)"""".toRegex().find(itemStr)
            return match?.groupValues?.getOrNull(1)
        }
        fun extractAttrInt(name: String): Int? {
            val match = """$name=(\d+)""".toRegex().find(itemStr)
            return match?.groupValues?.getOrNull(1)?.toIntOrNull()
        }

        val id = extractAttrInt("id") ?: return null
        val title = extractAttr("title") ?: return null
        val poster = extractAttr("poster") ?: ""

        return RailItem(
            uid = "coherence:$id",
            type = "coherence",
            content = poster,
            label = title,
            programId = id.toString()
        )
    }
}
